export type HospitalPlace = "КС" | "ДС";

export interface HospitalOrg {
  mis_id: string;
  org_name: string;
}

export interface HospitalAggPlaceItem {
  place: string;
  total_count: number;
}

export interface HospitalAggYearFundingItem {
  year: number;
  finance_source_id: number | null;
  finance_source_name: string;
  total_count: number;
}

export interface HospitalAggOutcomeYearItem {
  year: number;
  treatment_outcome_id: number | null;
  treatment_outcome_name: string;
  total_count: number;
}

export interface HospitalAggAvgBedDaysYearItem {
  year: number;
  avg_bed_days: number;
}

export interface HospitalAggAvgBedDaysMonthItem {
  year: number;
  month: number;
  avg_bed_days: number;
}

export interface HospitalAggAvgBedDaysDayItem {
  date: string;
  avg_bed_days: number;
}

export interface HospitalAggCasesMonthItem {
  year: number;
  month: number;
  total_count: number;
}

export interface HospitalAggCasesDayItem {
  date: string;
  total_count: number;
}

export interface HospitalAggBedProfileYearItem {
  bed_profile_id: number | null;
  bed_profile_name: string;
  total_count: number;
}

export interface HospitalAggBedProfileMonthItem {
  year: number;
  month: number;
  total_count: number;
}

export interface HospitalAggBedProfileDayItem {
  date: string;
  total_count: number;
}

export interface HospitalAggMkb10YearItem {
  mkb10_id: number | null;
  mkb10_code: string;
  mkb10_name: string;
  total_count: number;
}

export interface HospitalAggMkb10MonthItem {
  year: number;
  month: number;
  total_count: number;
}

export interface HospitalAggMkb10DayItem {
  date: string;
  total_count: number;
}

export interface HospitalAggDepartmentItem {
  department_id: number | null;
  department_name: string;
  total_count: number;
}

export interface HospitalAggDoctorItem {
  performer_employee_id: string | null;
  doctor_name: string;
  total_count: number;
}

export interface HospitalAggDoctorMonthItem {
  year: number;
  month: number;
  total_count: number;
}

export interface HospitalAggDoctorDayItem {
  date: string;
  total_count: number;
}

export interface HospitalBootstrapResponse {
  available_mis_ids: string[];
  available_orgs: HospitalOrg[];
  place_chart: HospitalAggPlaceItem[];
  year_funding_chart: HospitalAggYearFundingItem[];
}

export interface HospitalFilters {
  mis_id: string | null;
  place: HospitalPlace | null;

  finance_source_id: number | null;
  treatment_outcome_id: number | null;

  year: number | null;
  month: number | null;
  day: number | null;
  date_from: string | null;
  date_to: string | null;

  year_b: number | null;
  month_b: number | null;

  year_p: number | null;
  bed_profile_id: number | null;
  month_p: number | null;

  year_m: number | null;
  mkb10_id: number | null;
  month_m: number | null;

  year_d: number | null;
  department_id: number | null;
  performer_employee_id: string | null;
  month_d: number | null;
}

export interface HospitalChartsState {
  bootstrap: HospitalBootstrapResponse | null;

  slide1_place: HospitalAggPlaceItem[];
  slide2_yearFunding: HospitalAggYearFundingItem[];
  slide3_outcomeYear: HospitalAggOutcomeYearItem[];

  slide4_bedDaysYear: HospitalAggAvgBedDaysYearItem[];
  slide41_bedDaysMonth: HospitalAggAvgBedDaysMonthItem[];
  slide42_bedDaysDay: HospitalAggAvgBedDaysDayItem[];

  slide5_casesMonth: HospitalAggCasesMonthItem[];
  slide6_casesDay: HospitalAggCasesDayItem[];

  slide7_bedProfileYear: HospitalAggBedProfileYearItem[];
  slide71_bedProfileMonth: HospitalAggBedProfileMonthItem[];
  slide72_bedProfileDay: HospitalAggBedProfileDayItem[];

  slide8_mkb10Year: HospitalAggMkb10YearItem[];
  slide81_mkb10Month: HospitalAggMkb10MonthItem[];
  slide82_mkb10Day: HospitalAggMkb10DayItem[];

  slide9_departments: HospitalAggDepartmentItem[];
  slide10_doctors: HospitalAggDoctorItem[];
  slide11_doctorMonth: HospitalAggDoctorMonthItem[];
  slide12_doctorDay: HospitalAggDoctorDayItem[];
}

export interface HospitalLoadingState {
  bootstrap: boolean;

  slide1_place: boolean;
  slide2_yearFunding: boolean;
  slide3_outcomeYear: boolean;

  slide4_bedDaysYear: boolean;
  slide41_bedDaysMonth: boolean;
  slide42_bedDaysDay: boolean;

  slide5_casesMonth: boolean;
  slide6_casesDay: boolean;

  slide7_bedProfileYear: boolean;
  slide71_bedProfileMonth: boolean;
  slide72_bedProfileDay: boolean;

  slide8_mkb10Year: boolean;
  slide81_mkb10Month: boolean;
  slide82_mkb10Day: boolean;

  slide9_departments: boolean;
  slide10_doctors: boolean;
  slide11_doctorMonth: boolean;
  slide12_doctorDay: boolean;
}

export interface HospitalErrorState {
  bootstrap: string | null;

  slide1_place: string | null;
  slide2_yearFunding: string | null;
  slide3_outcomeYear: string | null;

  slide4_bedDaysYear: string | null;
  slide41_bedDaysMonth: string | null;
  slide42_bedDaysDay: string | null;

  slide5_casesMonth: string | null;
  slide6_casesDay: string | null;

  slide7_bedProfileYear: string | null;
  slide71_bedProfileMonth: string | null;
  slide72_bedProfileDay: string | null;

  slide8_mkb10Year: string | null;
  slide81_mkb10Month: string | null;
  slide82_mkb10Day: string | null;

  slide9_departments: string | null;
  slide10_doctors: string | null;
  slide11_doctorMonth: string | null;
  slide12_doctorDay: string | null;
}

export interface HospitalDashboardState {
  filters: HospitalFilters;
  charts: HospitalChartsState;
  loading: HospitalLoadingState;
  errors: HospitalErrorState;
}

export const createInitialHospitalFilters = (): HospitalFilters => ({
  mis_id: null,
  place: null,

  finance_source_id: null,
  treatment_outcome_id: null,

  year: null,
  month: null,
  day: null,
  date_from: null,
  date_to: null,

  year_b: null,
  month_b: null,

  year_p: null,
  bed_profile_id: null,
  month_p: null,

  year_m: null,
  mkb10_id: null,
  month_m: null,

  year_d: null,
  department_id: null,
  performer_employee_id: null,
  month_d: null,
});

export const createInitialHospitalChartsState = (): HospitalChartsState => ({
  bootstrap: null,

  slide1_place: [],
  slide2_yearFunding: [],
  slide3_outcomeYear: [],

  slide4_bedDaysYear: [],
  slide41_bedDaysMonth: [],
  slide42_bedDaysDay: [],

  slide5_casesMonth: [],
  slide6_casesDay: [],

  slide7_bedProfileYear: [],
  slide71_bedProfileMonth: [],
  slide72_bedProfileDay: [],

  slide8_mkb10Year: [],
  slide81_mkb10Month: [],
  slide82_mkb10Day: [],

  slide9_departments: [],
  slide10_doctors: [],
  slide11_doctorMonth: [],
  slide12_doctorDay: [],
});

export const createInitialHospitalLoadingState = (): HospitalLoadingState => ({
  bootstrap: false,

  slide1_place: false,
  slide2_yearFunding: false,
  slide3_outcomeYear: false,

  slide4_bedDaysYear: false,
  slide41_bedDaysMonth: false,
  slide42_bedDaysDay: false,

  slide5_casesMonth: false,
  slide6_casesDay: false,

  slide7_bedProfileYear: false,
  slide71_bedProfileMonth: false,
  slide72_bedProfileDay: false,

  slide8_mkb10Year: false,
  slide81_mkb10Month: false,
  slide82_mkb10Day: false,

  slide9_departments: false,
  slide10_doctors: false,
  slide11_doctorMonth: false,
  slide12_doctorDay: false,
});

export const createInitialHospitalErrorState = (): HospitalErrorState => ({
  bootstrap: null,

  slide1_place: null,
  slide2_yearFunding: null,
  slide3_outcomeYear: null,

  slide4_bedDaysYear: null,
  slide41_bedDaysMonth: null,
  slide42_bedDaysDay: null,

  slide5_casesMonth: null,
  slide6_casesDay: null,

  slide7_bedProfileYear: null,
  slide71_bedProfileMonth: null,
  slide72_bedProfileDay: null,

  slide8_mkb10Year: null,
  slide81_mkb10Month: null,
  slide82_mkb10Day: null,

  slide9_departments: null,
  slide10_doctors: null,
  slide11_doctorMonth: null,
  slide12_doctorDay: null,
});

export const createInitialHospitalDashboardState = (): HospitalDashboardState => ({
  filters: createInitialHospitalFilters(),
  charts: createInitialHospitalChartsState(),
  loading: createInitialHospitalLoadingState(),
  errors: createInitialHospitalErrorState(),
});

export const MONTH_LABELS_RU = [
  "",
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

export function getMonthLabel(month: number): string {
  return MONTH_LABELS_RU[month] ?? String(month);
}
